import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useChats } from '../store/chats.ts'
import { isUnavailable, type AnyCard } from '../api/types.ts'
import { Icon, type IconName } from './Icon.tsx'

/**
 * Карточка раздела внутри сообщения.
 *
 * Клиент намеренно ничего не знает о разделах: он рисует то, что прислал
 * сервер — заголовок, подпись, значок и список действий. Новый раздел
 * появляется в переписке без единой правки здесь.
 */

/**
 * Цвет у каждого раздела свой — по нему карточка в переписке узнаётся
 * раньше, чем прочитан заголовок. Значок тот же, что и в полосе
 * разделов: одна и та же вещь не должна выглядеть в двух местах
 * по-разному.
 */
const SECTIONS: Record<string, { icon: IconName; color: string; label: string }> = {
  shopping: { icon: 'shopping', color: '#4dcd5e', label: 'Покупки' },
  planner: { icon: 'planner', color: '#3390ec', label: 'Планер' },
  movies: { icon: 'movies', color: '#a695e7', label: 'Кино' },
  wishlist: { icon: 'wishes', color: '#ee7aae', label: 'Желания' },
  health: { icon: 'health', color: '#e17076', label: 'Здоровье' },
  wallet: { icon: 'wallet', color: '#faa774', label: 'Кошелёк' },
  recipes: { icon: 'recipes', color: '#e5ca77', label: 'Рецепты' },
}

export function EntityCard({ card }: { card: AnyCard }) {
  const navigate = useNavigate()
  const runCardAction = useChats((s) => s.runCardAction)
  const [busy, setBusy] = useState<string | null>(null)
  const [failed, setFailed] = useState<string | null>(null)

  if (isUnavailable(card)) {
    return (
      <div className="card card-gone">
        <div className="card-title">{card.title}</div>
        <div className="card-note">Запись удалена или закрыта</div>
      </div>
    )
  }

  const section = SECTIONS[card.section] ?? {
    icon: 'documents' as IconName,
    color: '#707579',
    label: 'Запись',
  }

  const act = async (actionId: string) => {
    if (actionId === 'open') {
      navigate(card.href)
      return
    }

    setBusy(actionId)
    setFailed(null)
    try {
      await runCardAction(card.entityType, card.entityId, actionId)
    } catch (error) {
      setFailed(error instanceof Error ? error.message : 'Не получилось')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="card" style={{ ['--section-color' as string]: section.color }}>
      <div className="card-head">
        <span className="card-icon" aria-hidden="true">
          <Icon name={section.icon} size={16} />
        </span>
        <span className="card-section">{section.label}</span>
      </div>

      <div className="card-body">
        {card.imageUrl && <img className="card-poster" src={card.imageUrl} alt="" loading="lazy" />}

        <div className="card-text">
          <div className={'card-title' + (card.done ? ' is-done' : '')}>
            {card.title}
            {card.badge && <span className="card-badge">{card.badge}</span>}
          </div>

          {card.subtitle && <div className="card-subtitle">{card.subtitle}</div>}

          {card.progress && card.progress.total > 0 && (
            <div className="card-progress">
              <div
                className="card-progress-fill"
                style={{ width: `${(card.progress.done / card.progress.total) * 100}%` }}
              />
              <span className="card-progress-text">
                {card.progress.done} из {card.progress.total}
              </span>
            </div>
          )}
        </div>
      </div>

      {failed && <div className="card-error">{failed}</div>}

      {card.actions.length > 0 && (
        <div className="card-actions">
          {card.actions.map((action) => (
            <button
              key={action.id}
              className={'card-action' + (action.style === 'primary' ? ' is-primary' : '')}
              onClick={() => void act(action.id)}
              disabled={busy !== null}
            >
              {busy === action.id ? '…' : action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
